import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { Tooltip } from '@dynamika-shared/ui/Tooltip/Tooltip';
import { notificationTypesReadable } from '@dynamika-shared/types/subscription';
import { Avatar } from 'src/components/Avatar/Avatar';
import { RECIPIENT_TYPE_LABEL } from '../constants';
import { EventTag } from '../components/EventTag';
import { SiteRecipientRow } from './siteRecipients.types';

// NOTE: a recipient can hold all eight events. Extras collapse into a `+N` pill
// with the full list on hover, as the Configuration tab's Scope column does.
const VISIBLE_EVENTS = 2;

const loadingSkeleton = (width: string) => (
  <div className={`bg-brightGray h-2 ${width} animate-pulse rounded-[5px]`} />
);

const cellText = (value?: string | null) => (
  <span className='text-1624 text-black whitespace-nowrap font-sans font-medium'>
    {value || '-'}
  </span>
);

export const makeSiteRecipientsColumns = (): ColumnType<SiteRecipientRow>[] => [
  {
    name: 'fullName',
    header: () => 'Recipient',
    loading: index =>
      loadingSkeleton(Number(index) % 2 ? 'w-[20rem]' : 'w-[17rem]'),
    getValue: row => (
      <div className='flex items-center gap-1.5'>
        <div className='bg-ghostWhite flex h-[3.2rem] w-[3.2rem] flex-shrink-0 items-center justify-center overflow-hidden rounded-full'>
          <Avatar
            className='text-black bg-brightGray'
            userName={row.fullName || row.email}
          />
        </div>
        <div className='flex flex-col'>
          <span className='text-1616 text-black font-sans font-semibold'>
            {row.fullName || '-'}
          </span>
          {row.email && (
            <span className='text-1216 text-dimGray font-nunito'>
              {row.email}
            </span>
          )}
        </div>
      </div>
    ),
    getCsvValue: row => row.fullName || row.email,
    hasSorting: true,
    thClassName: 'w-[28rem] min-w-[28rem]',
  },
  {
    name: 'recipientType',
    header: () => 'Recipient Type',
    loading: () => loadingSkeleton('w-[15rem]'),
    getValue: row => cellText(RECIPIENT_TYPE_LABEL[row.recipientType]),
    getCsvValue: row => RECIPIENT_TYPE_LABEL[row.recipientType] ?? '',
    hasSorting: true,
    thClassName: 'w-[24rem] min-w-[24rem]',
  },
  {
    name: 'role',
    header: () => 'Recipient Role',
    loading: () => loadingSkeleton('w-[15rem]'),
    getValue: row => cellText(row.role),
    getCsvValue: row => row.role,
    hasSorting: true,
    thClassName: 'w-[24rem] min-w-[24rem]',
  },
  {
    name: 'events',
    header: () => 'Associated Notification Event(s)',
    loading: () => loadingSkeleton('w-[24rem]'),
    getValue: row => {
      if (!row.events.length) return cellText();

      const labels = row.events.map(event => notificationTypesReadable[event]);
      const visible = labels.slice(0, VISIBLE_EVENTS);
      const hiddenCount = labels.length - visible.length;

      return (
        <div className='flex items-center gap-1'>
          {visible.map(label => (
            <EventTag key={label} label={label} />
          ))}
          {hiddenCount > 0 && (
            <Tooltip
              when
              title={
                <span className='text-black'>
                  {labels.slice(VISIBLE_EVENTS).join(', ')}
                </span>
              }
            >
              {/* NOTE: the span is load-bearing — antd Tooltip attaches its
                  hover trigger through a ref on the child, and `EventTag` is a
                  plain function component that cannot hold one. Without a DOM
                  element here the tooltip silently never opens. */}
              <span className='flex'>
                <EventTag label={`+${hiddenCount}`} />
              </span>
            </Tooltip>
          )}
        </div>
      );
    },
    getCsvValue: row =>
      row.events.map(event => notificationTypesReadable[event]).join(', '),
    hasSorting: true,
    thClassName: 'min-w-[36rem]',
  },
];
